import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { portfolioProjectsAlt } from '../mockAlt';
import { ArrowUpRight } from 'lucide-react';
import { motion } from 'framer-motion';

const PortfolioAlt = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', ...new Set(portfolioProjectsAlt.map(p => p.category))];

  const filteredProjects = selectedCategory === 'all'
    ? portfolioProjectsAlt
    : portfolioProjectsAlt.filter(p => p.category === selectedCategory);

  return (
    <div className="bg-white min-h-screen pt-20">
      {/* Header */}
      <section className="py-20 px-6 lg:px-12">
        <div className="max-w-screen-2xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-8xl lg:text-[12vw] font-black uppercase leading-none mb-12"
          >
            OUR WORK
          </motion.h1>

          {/* Filter Pills */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-wrap gap-3"
          >
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 font-bold uppercase text-sm tracking-wider transition-all ${
                  selectedCategory === category
                    ? 'bg-black text-white'
                    : 'bg-gray-100 text-black hover:bg-gray-200'
                }`}
              >
                {category === 'all' ? 'All Projects' : category}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="pb-24 px-6 lg:px-12">
        <div className="max-w-screen-2xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={`/alt/portfolio/${project.id}`}
                  className="group block"
                >
                  {/* Image */}
                  <div className="relative overflow-hidden bg-black mb-6">
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.4 }}
                      className="aspect-[4/3]"
                    >
                      <img
                        src={project.thumbnailUrl}
                        alt={project.title}
                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                      />
                    </motion.div>
                    {/* Color accent on hover */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 0.15 }}
                      className="absolute inset-0"
                      style={{ backgroundColor: project.color }}
                    />
                    {/* Impact badge */}
                    <div className="absolute top-6 left-6 bg-white text-black px-4 py-2 font-black text-xs uppercase">
                      {project.impact}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                        {project.category} • {project.year}
                      </div>
                      <h3 className="text-3xl lg:text-4xl font-black uppercase leading-tight mb-2">
                        {project.title}
                      </h3>
                      <p className="text-lg font-semibold text-gray-600 mb-3">{project.subtitle}</p>
                      <p className="text-base text-gray-600">{project.description}</p>
                    </div>
                    <ArrowUpRight 
                      className="flex-shrink-0 text-gray-400 group-hover:text-black group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" 
                      size={32} 
                    />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-20">
              <p className="text-2xl font-bold text-gray-400">No projects found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Bar */}
      <section className="py-16 px-6 lg:px-12 bg-black text-white">
        <div className="max-w-screen-2xl mx-auto flex flex-col lg:flex-row justify-between items-center gap-8">
          <h2 className="text-4xl lg:text-5xl font-black uppercase">
            HAVE A PROJECT?
          </h2>
          <Link
            to="/contact"
            className="inline-flex items-center bg-white text-black px-8 py-4 font-black uppercase hover:bg-gray-200 transition-colors group"
          >
            LET'S TALK
            <ArrowUpRight className="ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PortfolioAlt;
